import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../../shared/components/Icon';
import RetentionFilterPopup from './components/RetentionFilterPopup';
import RetentionMetricsCards from './components/RetentionMetricsCards';
import RetentionActivityChart from './components/RetentionActivityChart';
import CohortAnalysisTable from './components/CohortAnalysisTable';
import { useOverviewFilters } from '../../../../shared/context/OverviewFilterContext';
import axiosInstance from '../../../../shared/services/api/axiosInstance';

const Retention = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { appliedFilters, updateFilters, hasActiveFilters, teamId } = useOverviewFilters();
  
  // Data states
  const [retentionMetrics, setRetentionMetrics] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [timeRange, setTimeRange] = useState('monthly');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const handleFiltersChange = (filters) => {
    updateFilters(filters);
  };

  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentColor: "#1d0c46",
    borderColor: "#DDE2EE"
  };

  // Check if filters are active (considering dataMode)
  const checkActiveFilters = () => {
    if (!appliedFilters) return false;
    const dataMode = appliedFilters.dataMode || 'users';
    if (dataMode === 'users') {
      return appliedFilters.websites && appliedFilters.websites.length > 0;
    } else {
      return appliedFilters.contracts && appliedFilters.contracts.length > 0;
    }
  };

  // Fetch retention metrics (DAU, WAU, MAU / DAW, WAW, MAW)
  const fetchRetentionMetrics = useCallback(async () => {
    try {
      if (!teamId) {
        const dataMode = appliedFilters?.dataMode || 'users';
        if (dataMode === 'wallets') {
          setRetentionMetrics({ daw: 0, waw: 0, maw: 0 });
        } else {
          setRetentionMetrics({ dau: 0, wau: 0, mau: 0 });
        }
        return;
      }

      const dataMode = appliedFilters?.dataMode || 'users';
      const params = { team_id: teamId, dataMode };

      // Add website filter if selected (only for users mode)
      if (dataMode === 'users' && appliedFilters.websites && appliedFilters.websites.length > 0) {
        params.websites = appliedFilters.websites.join(',');
      }

      // Add contracts filter if selected (only for wallets mode)
      if (dataMode === 'wallets' && appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id).join(',');
      }

      // Add date range if specified
      if (appliedFilters.dateRange?.start) {
        params.from = appliedFilters.dateRange.start;
        params.to = appliedFilters.dateRange.end;
      }

      const response = await axiosInstance.get('/overview/retention-metrics', { params });

      if (response.data?.success) {
        setRetentionMetrics(response.data.retentionMetrics);
      } else {
        console.warn('Retention metrics response not successful:', response.data);
        if (dataMode === 'wallets') {
          setRetentionMetrics({ daw: 0, waw: 0, maw: 0 });
        } else {
          setRetentionMetrics({ dau: 0, wau: 0, mau: 0 });
        }
      }

    } catch (error) {
      console.error('Error fetching retention metrics:', error);
      setError(error.message);
      const dataMode = appliedFilters?.dataMode || 'users';
      if (dataMode === 'wallets') {
        setRetentionMetrics({ daw: 0, waw: 0, maw: 0 });
      } else {
        setRetentionMetrics({ dau: 0, wau: 0, mau: 0 });
      }
    }
  }, [appliedFilters, teamId]);

  // Fetch activity chart data
  const fetchActivityChart = useCallback(async () => {
    try {
      if (!teamId) {
        setActivityData([]);
        return;
      }

      const dataMode = appliedFilters?.dataMode || 'users';
      const params = { 
        team_id: teamId,
        timeRange: timeRange,
        dataMode
      };

      // Add website filter if selected (only for users mode)
      if (dataMode === 'users' && appliedFilters.websites && appliedFilters.websites.length > 0) {
        params.websites = appliedFilters.websites.join(',');
      }

      // Add contracts filter if selected (only for wallets mode)
      if (dataMode === 'wallets' && appliedFilters.contracts && appliedFilters.contracts.length > 0) {
        params.contracts = appliedFilters.contracts.map(c => c.id).join(',');
      }

      const response = await axiosInstance.get('/overview/retention-activity-chart', { params });

      if (response.data?.success) {
        setActivityData(response.data.activityData || []);
      } else {
        console.warn('Retention activity chart response not successful:', response.data);
        setActivityData([]);
      }

    } catch (error) {
      console.error('Error fetching retention activity chart:', error);
      setActivityData([]);
    }
  }, [appliedFilters, teamId, timeRange]);

  // Fetch data when filters or teamId change
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      await Promise.all([
        fetchRetentionMetrics(),
        fetchActivityChart()
      ]);
      
      setIsLoading(false);
    };

    fetchData();
  }, [fetchRetentionMetrics, fetchActivityChart]);

  return (
    <div className="relative h-full w-full overflow-y-auto">
      {/* Filter Button - Vertical, fixed to right edge */}
      <button
        onClick={() => setIsFilterOpen(true)}
        className="fixed z-10 transition-colors shadow-sm flex items-center justify-center gap-2 bg-white/80 backdrop-blur-sm hover:bg-white text-[#475569]"
        style={{ 
          fontFamily: 'Raleway, sans-serif',
          right: 0,
          top: '16px',
          padding: '16px 8px',
          writingMode: 'vertical-rl',
          textOrientation: 'mixed',
          transform: 'none',
          border: '1px solid #DDE2EE',
          borderRight: 'none',
          borderRadius: 0
        }}
      >
        <Icon name="filter" size={18} style={{ transform: 'rotate(90deg)' }} />
        <span className="text-sm font-medium">Filters</span>
        {checkActiveFilters() && (
          <span className="text-xs opacity-90">•</span>
        )}
      </button>

      {/* Page Content */}
      <div className="p-4 md:p-6 lg:p-8 pr-20">
        {/* Retention Metrics Cards */}
        <RetentionMetricsCards 
          data={retentionMetrics} 
          isLoading={isLoading} 
          dataMode={appliedFilters?.dataMode || 'users'}
        />

        {/* Users Over Time Chart */}
        <div className="p-4 md:p-6" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium" style={{ color: styles.accentColor, fontFamily: 'Raleway, sans-serif' }}>
              {(appliedFilters?.dataMode || 'users') === 'wallets' ? 'Wallets Over Time' : 'Users Over Time'}
            </h3>
            {/* Time Range Toggle */}
            <div className="flex border overflow-hidden" style={{ borderColor: styles.borderColor }}>
              {['weekly', 'monthly', 'yearly'].map((range) => (
                <button
                  key={range}
                  onClick={() => setTimeRange(range)}
                  className="px-3 py-1 text-xs transition-colors"
                  style={{
                    fontFamily: 'Raleway, sans-serif',
                    backgroundColor: timeRange === range ? styles.accentColor : styles.primaryBg,
                    color: timeRange === range ? '#ffffff' : styles.textPrimary
                  }}
                >
                  {range.charAt(0).toUpperCase() + range.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <div className="h-80">
            <RetentionActivityChart 
              data={activityData} 
              timeRange={timeRange} 
              isLoading={isLoading}
              dataMode={appliedFilters?.dataMode || 'users'}
            />
          </div>
        </div>

        {/* Cohort Analysis Table - Only show in Users mode */}
        {(appliedFilters?.dataMode || 'users') === 'users' && (
          <CohortAnalysisTable 
            teamId={teamId}
            appliedFilters={appliedFilters}
          />
        )}
      </div>

      {/* Filter Popup */}
      <RetentionFilterPopup 
        isOpen={isFilterOpen} 
        onClose={() => setIsFilterOpen(false)}
        onFiltersChange={handleFiltersChange}
        appliedFilters={appliedFilters}
        teamId={teamId || localStorage.getItem('selectedTeam') || 'Cryptique'}
      />
    </div>
  );
};

export default Retention;

